import { createContext, useContext } from 'react';

import type { CourseImageSizeOverride } from '@/features/courses/courseDisplaySettings';

// The course-level image size override in force where an image is rendering.
//
// Provided rather than passed as a prop because ImageUnit is reached through the
// shared ContentUnit dispatcher: threading a prop would touch every unit type
// for one unit type's benefit.
//
// The value carries BOTH resolved overrides because the two render boundaries
// know different things. ContentPage has the course settings and can resolve
// both; QuestionContent has neither the course nor a courseId, but does know it
// is rendering a question — so it switches `active` to `question` without ever
// having to look a setting up.
//
// ImageUnit reads `active` alone and never learns what a question is.
export interface ImageSizeOverrideValue {
  /** What applies to images rendered under this provider. */
  active: CourseImageSizeOverride;
  /** What a question boundary beneath this provider should switch to. */
  question: CourseImageSizeOverride;
}

// 'authored' on both sides, so an ImageUnit rendered outside any provider — the
// editor preview, a test, anything added later — keeps authored behaviour
// without needing to know this feature exists. A forgotten provider degrades to
// today's rendering rather than to a wrong size.
const NO_OVERRIDE: ImageSizeOverrideValue = {
  active: 'authored',
  question: 'authored',
};

const ImageSizeOverrideContext = createContext<ImageSizeOverrideValue>(NO_OVERRIDE);

export const ImageSizeOverrideProvider = ImageSizeOverrideContext.Provider;

/** The override applying to images rendered here. */
export function useImageSizeOverride(): CourseImageSizeOverride {
  return useContext(ImageSizeOverrideContext).active;
}

/**
 * The value a question boundary should provide: the question-scoped override
 * becomes the active one, and stays available to any nested question.
 */
export function useQuestionImageSizeOverrideValue(): ImageSizeOverrideValue {
  const { question } = useContext(ImageSizeOverrideContext);
  return { active: question, question };
}
